/**
 * @file The root route's layout: the auth decision and the top bar.
 *
 * The SIDEBAR lives in `WorkspaceLayout`, one route below this one — it needs to
 * know which portfolio you are in, and `/` has no portfolio yet. The HEADER lives
 * here, because it must appear on every screen including onboarding: it carries
 * the signed-in person's name and the sign-out control, and a signed-out or
 * portfolio-less visitor still needs to see the product mark.
 *
 * `isLoaded` is checked first so a signed-in user never sees the sign-in form
 * flash while the session resolves.
 *
 * The content below is deliberately unpadded and unwidthed: each route owns its
 * own container, so `/` can be a narrow onboarding column while the dashboard uses
 * the full width. A single `max-w-3xl` here is what made every page a strip.
 */
import { Outlet, useRouterState } from "@tanstack/react-router";
import { useAuthValue } from "./auth/context";
import { AppHeader } from "./components/AppHeader";
import { CenteredPanel, Spinner } from "./components/ui";
import { VerifyEmailBanner } from "./components/VerifyEmailBanner";

/** The signed-out page an emailed reset link opens. Every other path shows the sign-in screen. */
export const RESET_PASSWORD_PATH = "/reset-password";

export function AppShell() {
  const { session, AuthScreen } = useAuthValue();
  const isPublicPath = useRouterState({ select: (s) => s.location.pathname === RESET_PASSWORD_PATH });

  if (!session.isLoaded) {
    return (
      <div className="flex min-h-dvh flex-col">
        <AppHeader />
        <div className="mx-auto w-full max-w-3xl px-4 py-8">
          <CenteredPanel>
            <Spinner />
            Loading…
          </CenteredPanel>
        </div>
      </div>
    );
  }

  if (!session.isSignedIn) {
    return (
      <div className="flex min-h-dvh flex-col">
        <AppHeader />
        <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-8">
          {/* The ONE public route: the page an emailed reset link lands on. */}
          {isPublicPath ? <Outlet /> : <AuthScreen />}
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col">
      <AppHeader />
      <VerifyEmailBanner />
      <Outlet />
    </div>
  );
}
